import notificationService from '@/services/notificationService'

// How often the navbar bell re-checks the badge. The endpoint is a single
// COUNT, so this is cheap; it is still the only polling the panel does.
export const UNREAD_POLL_INTERVAL = 60000

// Rows shown inside the dropdown itself — the rest live on the full page.
const PREVIEW_SIZE = 8

/**
 * The admin notification bell.
 *
 * Rows are shared by every admin (read state is global, see the backend's
 * admin_notifications migration), so nothing here is scoped to the logged-in
 * user — two admins looking at the panel see the same badge.
 */
export default {
  namespaced: true,
  state: {
    // Preview rows for the dropdown, newest first.
    items: [],
    unread: 0,
    loading: false,
  },
  getters: {
    unread: state => state.unread,
    // The bell caps the badge rather than breaking the layout.
    badge: state => (state.unread > 99 ? '99+' : String(state.unread)),
    hasUnread: state => state.unread > 0,
    items: state => state.items,
  },
  mutations: {
    SET_ITEMS(state, items) {
      state.items = items
    },
    SET_UNREAD(state, unread) {
      state.unread = Number(unread) || 0
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    MARK_READ(state, ids) {
      const readAt = new Date().toISOString()
      state.items = state.items.map(item => (ids.includes(item.id)
        ? { ...item, is_read: true, read_at: readAt }
        : item))
    },
    MARK_ALL_READ(state) {
      const readAt = new Date().toISOString()
      state.items = state.items.map(item => ({ ...item, is_read: true, read_at: readAt }))
      state.unread = 0
    },
    REMOVE(state, ids) {
      state.items = state.items.filter(item => !ids.includes(item.id))
    },
  },
  actions: {
    /**
     * The badge. Called on a timer, so it never flips `loading` — a spinner
     * appearing every minute on its own would be noise.
     */
    fetchUnreadCount({ commit }) {
      return notificationService.unreadCount()
        .then(response => {
          commit('SET_UNREAD', response.data?.data?.unread ?? 0)
          return response
        })
    },

    /**
     * One page of rows. `params` accepts read / type / per_page / page.
     *
     * An empty listing comes back as 204 with no body (responseWithPagination),
     * hence the defaults rather than a straight read of response.data.
     */
    fetchNotifications({ commit }, params = {}) {
      commit('SET_LOADING', true)

      const { preview, ...query } = params

      return notificationService.list(query)
        .then(response => {
          const body = response.data || {}
          const result = {
            items: body.data || [],
            totalPage: body.total_page || 1,
            nextPage: body.next_page || null,
          }

          if (preview) commit('SET_ITEMS', result.items)

          return result
        })
        .finally(() => commit('SET_LOADING', false))
    },

    /** The dropdown's own list: the newest rows, whatever their read state. */
    fetchPreview({ dispatch }) {
      return dispatch('fetchNotifications', { per_page: PREVIEW_SIZE, preview: true })
    },

    markRead({ commit }, ids) {
      const notificationIds = [].concat(ids)

      return notificationService.markRead(notificationIds)
        .then(response => {
          commit('MARK_READ', notificationIds)
          // The server returns the authoritative badge; trust it over a local
          // decrement, which would drift whenever another admin is reading too.
          commit('SET_UNREAD', response.data?.data?.unread ?? 0)
          return response
        })
    },

    markAllRead({ commit }) {
      return notificationService.markAllRead()
        .then(response => {
          commit('MARK_ALL_READ')
          return response
        })
    },

    remove({ commit, dispatch }, ids) {
      const notificationIds = [].concat(ids)

      return notificationService.remove(notificationIds)
        .then(response => {
          commit('REMOVE', notificationIds)
          // A deleted row may have been unread; re-read the badge.
          dispatch('fetchUnreadCount')
          return response
        })
    },

    removeAll({ commit }) {
      return notificationService.removeAll()
        .then(response => {
          commit('SET_ITEMS', [])
          commit('SET_UNREAD', 0)
          return response
        })
    },
  },
}
